import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { select, Store } from '@ngrx/store';
import { ImageCroppedEvent } from 'ngx-image-cropper';
import { Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ActionService } from 'src/app/core/services/action.service';
import { AuthService } from 'src/app/core/services/auth.service';
import { CommonService } from 'src/app/core/services/common.service';
import { RouteService } from 'src/app/core/services/route.service';
import {
  CreateApplication,
  UpdateApplication
} from 'src/app/core/state/_actions/application.actions';
import { LoadCompanies } from 'src/app/core/state/_actions/company.actions';
import { Company } from 'src/app/core/state/_models/company.model';
import { OpTypes } from 'src/app/core/state/_models/general.model';
import { UpdateViewSettingsPayload } from 'src/app/core/state/_models/settings.models';
import {
  getApplicationOperations,
  getUserApplications
} from 'src/app/core/state/_selectors/application.selector';
import { getCompanies } from 'src/app/core/state/_selectors/company.selector';
import { ApplicationService } from 'src/app/core/state/_services/application.service';
import { remoteFileToBase64 } from 'src/app/core/utils/data-processor.util';
import { Roles } from 'src/app/shared/enums/role.enum';
import { DragUpload } from 'src/app/shared/models/components.models';
import { AlertService } from 'src/app/shared/services/alert.service';
import { SubSink } from 'subsink';

@Component({
  selector: 'app-application-new',
  templateUrl: './application-new.component.html',
  styleUrls: ['./application-new.component.scss'],
})
export class ApplicationNewComponent implements OnInit {
  iconBaseURL: string = '/assets/images/icons/default-app-icons/';
  companies: Array<Company> | null = null;
  icons: Array<string> = icons;
  applicationForm!: UntypedFormGroup;
  selectedIconBase64: string = '';
  dragValue!: DragUpload;
  imageChangedEvent: any;
  fileName: string = '';
  newLogoData: any = null;
  clearDragUpload = new Subject();
  deleteLogo = false;
  subsink: SubSink = new SubSink();

  constructor(
    private store: Store,
    private fb: UntypedFormBuilder,
    private authService: AuthService,
    private modal: NgbModal,
    private action: ActionService,
    private applicationService: ApplicationService,
    private route: RouteService,
    private commonService: CommonService
  ) {}

  
  ngOnInit(): void {
    this.initForm();
    if (this.isSuperadmin()) {
      this.store.dispatch(new LoadCompanies());
    } else {
      this.applicationForm.patchValue({
        CompanyKey: this.authService.userProfile?.CompanyKey,
      });
    }
    this.companiesSubscriber();
  }

  initForm = () => {
    this.applicationForm = this.fb.group({
      Name: [
        null,
        [
          Validators.required,
          Validators.pattern(/^([a-zA-Z0-9]+(\s+[a-zA-Z0-9]+)*){4,45}$/),
        ],
      ],
      CompanyKey: [null, [Validators.required]],
      Logo: [null],
      Icon:'',
      IsIcon:false,
      IconColor: this.commonService.defaultIconColor,
    });
  };

  /**
   * Load companies subscribers
   */
  companiesSubscriber = () => {
    this.subsink.add(
    this.store
      .pipe(
        select(getCompanies),
        filter((companies) => !!companies)
      )
      .subscribe((companies) => {
        this.companies = companies;
      }));
  };

  /**
   * Submit
   */
  onSubmit = async () => {
    if (this.authService.userProfile?.Role === Roles.admin) {
      this.applicationForm.controls['CompanyKey'].setValue(
        this.authService.userProfile.CompanyKey
      );
    }
    this.applicationForm.controls.Logo.setValue(this.selectedIconBase64);
    try {
      await this.action.dispatch(
        OpTypes.CREATE,
        new CreateApplication(this.applicationForm.value),
        getApplicationOperations
      );
      let name = '';
      let key = '';
      this.subsink.add(
      this.store.pipe(select(getUserApplications)).subscribe((data: any) => {
        if(this.newLogoData){
          this.newLogoData.set('applicationKey', '' + data[0].Key + '');
        }
        key = data[0].Key;
        name = data[0].Name;
      }));
      let payload = {
        Key: key,
        Logo: '',
        Icon:'',
        IsIcon:false,
        IconColor:this.commonService.defaultIconColor,
        Name: name.toString(),
        DefaultPageKey: '',
        DefaultDashboardKey: '',
        SelfRegistrationAllowed: false,
        //ParticipantAllowedToInviteUser:
        //this.form.value.ParticipantAllowedToInviteUser,
        ApplicationSetting:{
          ApplicationKey: this.route.getApplicationKey(),
          Color1: '#ee3452',
          Color2: '#012d68',
          Color3: '#fece18',
          Color4: '#f05ab4',
          Color5: '#168bdf',
          Color6: '#0083ff',
          Color7: '#00b385',
          BrandId: '',
        }
      };
      if (this.newLogoData) {
        this.applicationService
          .uploadLogo(this.newLogoData)
          .subscribe((res: any) => {
            payload.Logo = this.deleteLogo ? '' : res.FileName;
            this.updateApplication(payload);
          });
      }
      this.applicationForm.reset();
      this.close();
    } catch (err) {}
  };

  /**
   * Super admin check
   * @returns true if super admin
   */
  isSuperadmin = () => this.authService.userProfile?.Role === Roles.superAdmin;

  /**
   * @returns companies
   */
  getCompanies = (): Array<any> | null =>
    this.companies
      ? this.companies.map((company) => {
          return { value: company.Key, label: company.Name };
        }).sort((a,b) => {
          if (a.label.toLowerCase() < b.label.toLowerCase()) {
            return -1;
          }
          if (a.label.toLowerCase() > b.label.toLowerCase()) {
            return 1;
          }
          return 0;
        })
      : null;

  /**
   * Get application form controls
   */
  get controls() {
    return this.applicationForm.controls;
  }

  updateApplication(payload: UpdateViewSettingsPayload): void {
    this.store.dispatch(new UpdateApplication(payload));
  }
  /**
   * Close modal
   */
  close = () => {
    this.modal.dismissAll();
  };

  /**
   * Change logo
   */
  changeLogo = async () => {
    this.selectedIconBase64 = (<any>(
      await remoteFileToBase64(`${this.controls.Logo.value}`)
    )).value;
  };

  /**
   * Drag file event
   */
  dragFileEvent = (event: any) => {
    this.dragValue = event;
    if (this.dragValue.value.length) {
      this.fileName = event.value[0].name;
      this.imageChangedEvent = `data:image/png;base64,${this.dragValue.value[0].value}`;
    } else this.imageChangedEvent = '';
  };

  /**
   * Crop image event
   */
  imageCropped(event: ImageCroppedEvent) {
    // this.selectedIconBase64 = event.base64!;
    this.applicationForm.patchValue({
      Icon:'',
      IsIcon:false,
      IconColor:this.commonService.defaultIconColor,
    })
    this.uriToFile(event.base64!, this.fileName);
  }

  uriToFile = async (inputURL: string, fileName: string = '') => {
    this.deleteLogo = inputURL.includes('delete');
    const inputURI = inputURL.includes('data:')
      ? inputURL
      : (<any>await remoteFileToBase64(inputURL, false)).value;
    const arr = inputURI.split(','),
      mime = arr[0].match(/:(.*?);/)[1],
      bstr = atob(arr[1]);
    let n = bstr.length,
      u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    this.fileChangeEvent(
      {
        target: {
          files: [
            new File(
              [u8arr],
              fileName ? fileName : inputURL.split('/').slice(-1).join(),
              {
                type: mime,
              }
            ),
          ],
        },
      },
      false
    );
  };

  fileChangeEvent(event: any, imageChopper: boolean = true): void {
    this.fileName = event.target.files[0].name;
    if (imageChopper) this.imageChangedEvent = event;
    let formdata = new FormData();
    formdata.append('0', event.target.files[0]);
    // formdata.append('applicationId', this.route.getApplicationKey() + '');
    //this.uploadLogo(formdata);
    this.newLogoData = formdata;
  }
  uploadLogo = async (formdata: any) => {
    await this.applicationService
      .uploadLogo(formdata)
      .subscribe((res) => (this.selectedIconBase64 = res.FileName));
  };

  ngOnDestroy(){
    this.subsink.unsubscribe();
  }
}

export const icons: Array<string> = [
  'align_center_vertical.png',
  'app_recent.png',
  'apps_list.png',
  'arrow_routing.png',
  'arrow_routing_rectangle_multiple.png',
  'autosum.png',
  'badge.png',
  'bin_full.png',
  'book_open.png',
  'book_open_globe.png',
  'book_pulse.png',
  'text_align_distributed_evenly.png',
  'table.png',
  'star.png',
  'ic_fluent_stack_24_filled.png',
  'ic_fluent_shield_task_24_filled.png',
  'ic_fluent_shifts_24_filled.png',
  'ic_fluent_select_all_on_24_filled.png',
  'ic_fluent_library_24_filled.png',
  'ic_fluent_grid_dots_24_filled.png',
  'ic_fluent_folder_24_filled.png',
  'ic_fluent_document_page_bottom_center_24_filled.png',
  'ic_fluent_document_header_24_filled.png',
  'ic_fluent_document_copy_24_filled.png',
  'ic_fluent_document_bullet_list_24_filled.png',
  'ic_fluent_document_24_filled.png',
  'ic_fluent_data_funnel_24_filled.png',
  'ic_fluent_copy_24_filled.png',
  'ic_fluent_column_triple_24_filled.png',
  'ic_fluent_class_24_filled.png',
  'ic_fluent_checkmark_square_24_filled.png',
  'ic_fluent_channel_share_24_filled.png',
  'ic_fluent_box_edit_24_filled.png',
  'ic_fluent_broad_activity_feed_24_filled.png',
  'ic_fluent_book_information_24_filled.png',
  'ic_fluent_board_split_24_filled.png',
  'ic_fluent_board_24_filled.png',
  'ic_fluent_album.png',
  'ic_fluent_person_board_24_filled.png',
  'ic_fluent_shield_checkmark_24_filled.png',
  'ic_fluent_stack_star_24_filled.png',
  'ic_fluent_text_more_24_filled.png',
  'ic_fluent_ticket_diagonal_24_filled.png',
  'ic_fluent_ticket_horizontal_24_filled.png',
  'ic_fluent_timeline_24_filled.png',
  'ic_fluent_web_asset_24_filled.png',
  'ic_fluent_xbox_console_24_filled.png',
  'delete.png',
];
